import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { fetchJobs, bulkAssignJob } from '@/lib/api';
import { PIPELINE_STAGES, STAGE_LABELS } from '@/types';

interface BulkAssignJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateIds: string[];
  onSuccess: () => void;
}

export function BulkAssignJobDialog({
  open,
  onOpenChange,
  candidateIds,
  onSuccess,
}: BulkAssignJobDialogProps) {
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedStage, setSelectedStage] = useState('sourced');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: jobsData, isLoading: isLoadingJobs } = useQuery({
    queryKey: ['jobs', { limit: 100 }],
    queryFn: () => fetchJobs({ limit: 100 }),
    enabled: open,
  });

  // Paused jobs are still actively worked (only unpublished from the site),
  // so candidates can be assigned to them alongside open jobs.
  const jobs = (jobsData?.data ?? []).filter(
    (job) => job.status === 'open' || job.status === 'paused',
  );

  const mutation = useMutation({
    mutationFn: () => bulkAssignJob(candidateIds, selectedJobId, selectedStage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelectedJobId('');
      setSelectedStage('sourced');
      setError(null);
      onOpenChange(false);
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to assign candidates to job');
    },
  });

  const handleSubmit = () => {
    if (!selectedJobId) {
      setError('Please select a job');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  const handleClose = (value: boolean) => {
    if (!mutation.isPending) {
      setSelectedJobId('');
      setSelectedStage('sourced');
      setError(null);
      onOpenChange(value);
    }
  };

  // Filter out terminal stages for initial assignment
  const assignableStages = PIPELINE_STAGES.filter(
    (s) => s !== 'hired' && s !== 'disqualified',
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign to Job</DialogTitle>
          <DialogDescription>
            Assign {candidateIds.length} candidate{candidateIds.length !== 1 ? 's' : ''} to a job.
            Candidates already assigned to this job will be skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label>Job *</Label>
            {isLoadingJobs ? (
              <div className="flex items-center gap-2 text-sm text-text-tertiary">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading jobs...
              </div>
            ) : jobs.length === 0 ? (
              <p className="text-sm text-text-tertiary">No open or paused jobs available</p>
            ) : (
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}
                      {job.company ? ` - ${job.company.name}` : ''}
                      {job.status === 'paused' ? ' (Paused)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Initial Stage</Label>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableStages.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {STAGE_LABELS[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedJobId || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              `Assign ${candidateIds.length} Candidate${candidateIds.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
